/**
 * v1 Top-Up Dialog Component
 * Modal for adding tokens to user balance
 */

import { useState } from 'react'
import { useTopUp } from '../hooks/useTopUp'
import { User } from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Coins, AlertCircle } from 'lucide-react'

interface TopUpDialogProps {
  user: User
  onClose: () => void
}

export function TopUpDialog({ user, onClose }: TopUpDialogProps) {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState('')

  const { mutate: topUp, isPending } = useTopUp()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const parsedAmount = parseFloat(amount)

    if (isNaN(parsedAmount)) {
      setError('Please enter a valid number')
      return
    }

    if (parsedAmount <= 0) {
      setError('Amount must be greater than 0')
      return
    }

    topUp(
      { userId: user._id, amount: parsedAmount },
      {
        onSuccess: () => {
          onClose()
        }
      }
    )
  }

  const currentBalance = user.tokenCredits || 0

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Top-Up Balance
          </DialogTitle>
          <DialogDescription>
            Add tokens to {user.name || user.email}'s balance
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Info */}
          <div className="rounded-lg bg-muted p-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">User</span>
                <span className="text-sm">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Current Balance</span>
                <span className="text-sm font-mono">
                  {currentBalance.toFixed(2)} tokens
                </span>
              </div>
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount to Add <span className="text-destructive">*</span>
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter token amount (e.g., 1000)"
              required
              disabled={isPending}
              autoFocus
            />
            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <p className="text-sm text-muted-foreground">
                New balance will be:{' '}
                <span className="font-mono font-medium">
                  {(currentBalance + parseFloat(amount)).toFixed(2)} tokens
                </span>
              </p>
            )}
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div className="ml-2">{error}</div>
            </Alert>
          )}

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending || !amount || parseFloat(amount) <= 0}
            >
              {isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Processing...
                </>
              ) : (
                <>
                  <Coins className="mr-2 h-4 w-4" />
                  Top-Up
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
